import { ExtractDataTrackIdValues, setupDataTrackId } from './data-trackid-utils';

// The information in this file is the source of truth for values used as `data-trackid` attributes
// throughout the UI codebase. This helps keep track of all the existing tracking ids in a single
// place.
//
// This information is helpful for collaboration across eng and PM, so it is encouraged to add
// jsdoc to clarify what specific elements cover.
//
// These values are generally used by tools outside of the monorepo (e.g. fullstory, pendo), so must be modified and/or
// deleted with care to avoid breaking tracking. Collaborate with your PM, team, and other stakeholders who use these
// tools to avoid unintentional breakage of tracking.

// Data tracking ids for some global application features that are maintained by the spotlight team.
// DRI: Spotlight
const SPOTLIGHT_DATA_TRACK_IDS = setupDataTrackId({
  /**
   * Main navigation shown on all pages of the product.
   */
  mainNav: {
    /**
     * Element that contains the main navigation.
     */
    root: 'mainNav_root',
    /**
     * The button that toggles the expanded state.
     */
    expandButton: 'mainNav_expandButton',
  },
  userSettingsMenu: {
    /**
     * Element that contains the user settings menu.
     */
    root: 'userSettingsMenu_root',
  },
  timeRangeControls: {
    /**
     * Element that contains the time range controls. Used as the default `data-trackid` for the time range controls.
     */
    root: 'timeRangeControls_root',
  },
  test: {
    /**
     * Example tracking id you may use when writing tests that use of `data-trackid` to avoid
     * running into type errors that require the value come from the DATA_TRACK_IDS config.
     *
     * This tracking id should NOT be used in production code.
     */
    test: 'test_test',
  },
});

/**
 * Full set of data tracking ids. These should always be used when setting a `data-trackid` to ensure consistency and to
 * provide a single source of truth for these values.
 */
export const DATA_TRACK_IDS = {
  ...SPOTLIGHT_DATA_TRACK_IDS,
} as const;

export type DataTrackIds = typeof DATA_TRACK_IDS;

/**
 * Union of all the string values from `DATA_TRACK_IDS`, which can be used as values when setting `data-trackid`.
 */
export type DataTrackIdValues = ExtractDataTrackIdValues<typeof DATA_TRACK_IDS>;
